import React, { useState } from "react";
import { motion } from "framer-motion";
import { Download, ArrowDown, Loader2, MapPin } from "lucide-react";
import apiService from "../services/api";
import { useToast } from "../hooks/use-toast";

const Hero = ({ profile }) => {
  const { toast } = useToast();
  const [downloading, setDownloading] = useState(false);

  if (!profile) return null;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await apiService.downloadResume();
      toast({ title: "Resume downloaded", description: "Saurav_Sinha_Resume.pdf" });
    } catch (e) {
      toast({ title: "Download failed", description: "Please try again.", variant: "destructive" });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center pt-16 overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
        backgroundSize: "80px 80px"
      }} />

      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full py-24 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center relative">
        <motion.div
          className="lg:col-span-7"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <p className="font-mono text-xs tracking-[0.3em] text-emerald-400 mb-8" data-testid="hero-sec-level">
            [ SEC_LEVEL: ARCHITECT ] · NEW DELHI, IN
          </p>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-gray-100 mb-8" data-testid="hero-title">
            Saurav Sinha builds infrastructure
            <span className="italic text-emerald-400"> the way architects build vaults.</span>
          </h1>

          <p className="text-base text-gray-400 max-w-xl leading-relaxed mb-4" data-testid="hero-subtitle">
            {profile.title} — {profile.tagline}
          </p>
          <p className="text-sm text-gray-500 max-w-xl leading-relaxed mb-12">
            15+ years across global financial services. Currently leading IT infrastructure at
            Sumitomo Mitsui Banking Corporation. Security is never the afterthought — it is the load-bearing structure.
          </p>

          <div className="flex flex-wrap items-center gap-6">
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="bg-emerald-500 text-black hover:bg-emerald-400 transition-colors font-mono tracking-wider uppercase text-sm px-6 py-3 inline-flex items-center gap-2 disabled:opacity-60"
              aria-label="Download resume PDF"
              data-testid="hero-download-resume-btn"
            >
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Download Resume
            </button>
            <button
              onClick={() => document.getElementById("philosophy")?.scrollIntoView({ behavior: "smooth" })}
              className="font-mono tracking-wider uppercase text-sm px-6 py-3 border border-white/20 text-gray-100 hover:border-emerald-500/50 hover:text-emerald-400 transition-colors inline-flex items-center gap-2"
              data-testid="hero-read-story-btn"
            >
              Read The Story
              <ArrowDown className="h-4 w-4" />
            </button>
          </div>
        </motion.div>

        <motion.div
          className="lg:col-span-5 relative"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          <div className="relative max-w-md mx-auto lg:mx-0 group">
            <div className="absolute -inset-3 border border-emerald-500/30 translate-x-4 translate-y-4 pointer-events-none" />
            <div className="relative overflow-hidden border border-white/10">
              <img
                src={profile.photo}
                alt="Saurav Sinha"
                className="w-full h-auto object-cover grayscale group-hover:grayscale-0 transition-[filter] duration-700"
                data-testid="hero-photo"
              />
              <div className="absolute inset-0 bg-emerald-500/10 group-hover:bg-transparent transition-colors duration-700 pointer-events-none" />
            </div>
            <div className="absolute bottom-0 left-0 right-0 bg-black/80 backdrop-blur-sm border-t border-white/10 px-4 py-3 flex items-center justify-between">
              <span className="font-mono text-[10px] tracking-widest text-gray-400 uppercase">ID: S.SINHA — AVP, SMBC</span>
              <span className="font-mono text-[10px] tracking-widest text-emerald-400 flex items-center gap-1">
                <MapPin className="h-3 w-3" /> 28.61°N 77.20°E
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
